/* eslint-disable no-case-declarations */
import { useReducer, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as ACT from './actions'
import { ContactModal } from './ContactModal'
import { toPrioritiesSelect, toUsers, toStatusesSelect } from './serializers'
import { convertImgToBlob } from 'utils/helpers'
import { makeGetRequest } from 'utils/api'
import { getUser } from 'utils/authService'

const priorities = toPrioritiesSelect()

const initialContact = {
  firstName: '',
  lastName: '',
  title: '',
  company: null,
  priority: '',
  salutation: null,
  photo: null,
  phone: '',
  email: '',
  facebook: '',
  linkedin: '',
  twitter: '',
  instagram: '',
  cellPhone: '',
  about: '',
  fax: '',
  phoneExt: '',
  accountOwner: getUser()
    ? {
        value: getUser().id,
        label: getUser().name
      }
    : null
}

const requiredFields = ['firstName', 'lastName', 'company', 'email']

const reducer = (state, action) => {
  switch (action.type) {
    case ACT.CHANGE_CONTACT_FIELD:
      const { property, value } = action.payload
      return { ...state, [property]: value }
    case ACT.RESET_CONTACT:
      return { ...initialContact }
    case ACT.SET_CONTACT:
      const { contact } = action.payload
      return { ...contact }
    default:
      throw new Error()
  }
}

const ContactModalContainer = ({
  toggleContactModal,
  showContactModal,
  contact,
  actionType,
  currentCompany,
  isEdit
}) => {
  const [hasErrors, setHasErrors] = useState(false)
  const [file, setFile] = useState(null)
  const [crop, setCrop] = useState(null)
  const [editedContact, dispatchState] = useReducer(reducer, initialContact)
  const storedUsers = useSelector(state => state.users.users)
  const [users, setUsers] = useState(toUsers(storedUsers) || [])
  const [statuses, setStatuses] = useState([])

  const dispatch = useDispatch()

  useEffect(() => {
    if (users.length) return
    ;(async () => {
      const { content } = await makeGetRequest('/v1/users')
      setUsers(toUsers(content))
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (contact && contact.firstName) {
      const user = users.find(u => u.value === contact.accountOwner?.id)
      user && changeProperty(user, 'accountOwner')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users])

  useEffect(() => {
    if (!showContactModal) {
      clearContact()
    }
  }, [showContactModal])

  useEffect(() => {
    if (currentCompany?.value) {
      changeProperty(currentCompany, 'company')
    }
  }, [currentCompany])

  useEffect(() => {
    if (contact && contact.firstName) {
      dispatchState({
        type: ACT.SET_CONTACT,
        payload: {
          contact
        }
      })
    }

    if (contact && contact.photo) {
      convertImgToBlob(contact.photo, blob => {
        setFile(blob)
      })
    }
  }, [contact])

  useEffect(() => {
    ;(async () => {
      const response = await makeGetRequest('/v1/contact-status')
      setStatuses(toStatusesSelect(response))
    })()
  }, [])

  const changeProperty = (value, property) => {
    dispatchState({
      type: ACT.CHANGE_CONTACT_FIELD,
      payload: {
        property,
        value
      }
    })
  }

  const validateRequired = cb => {
    const emptyRequiredFields = requiredFields.filter(field => !editedContact[field])
    const error = !!emptyRequiredFields.length
    setHasErrors(error)
    if (!error) {
      cb()
    }
  }

  const clearContact = () => {
    dispatchState({
      type: ACT.RESET_CONTACT
    })
    setFile(null)
    setCrop(null)
    setHasErrors(false)
  }

  const onSave = () => {
    validateRequired(() => {
      if (!hasErrors) {
        dispatch({
          type: actionType,
          payload: {
            contact: editedContact,
            fileInfo: {
              file,
              crop
            },
            id: editedContact.id
          }
        })
        toggleContactModal()
      }
    })
  }

  return (
    <ContactModal
      contact={editedContact}
      showContactModal={showContactModal}
      toggleContactModal={toggleContactModal}
      changeProperty={changeProperty}
      priorities={priorities}
      statuses={statuses}
      saveContact={onSave}
      showContactError={hasErrors}
      setFile={setFile}
      setCrop={setCrop}
      currentCompany={currentCompany}
      users={users}
      isEdit={isEdit}
    />
  )
}

export default ContactModalContainer
